// covered-by: frontends/carrie/src/routes/ask-puntt/agentComponents/__tests__/ChatAtoms.test.tsx
import {
  ASK_PUNTT_NESTED_ONLY_CHAT_ATOM_IDS,
  type AskPunttChatAtom,
  type AskPunttChatAtomOf,
  type AskPunttUserQuestion,
} from "@mg/schemas/src/christo/askPuntt";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  readReviewPreflightState,
  writeReviewPreflightState,
} from "./reviewPreflightPersistence";

import { useAskPunttReview } from "../../../ask-v2/review/useAskPunttReview";
import { resolveReviewListCardThumbnail } from "../../../components/ask-puntt-review/resolveReviewListCardThumbnail";
import { ReviewListCard } from "../../../components/ask-puntt-review/ReviewListCard";
import { isDemoEnterprise } from "../../../utils/auth";
import { useAskPunttArtifactContext } from "../AskPunttArtifactContext";
import { MetricBlock } from "../components/canvas/blocks/MetricBlock";
import { EnterpriseImpact } from "../components/EnterpriseImpact";
import { FeedMemoryCard } from "../components/FeedMemoryCard";
import {
  type QuestionAttachment,
  QuestionForm,
} from "../components/QuestionForm";
import { ReviewPreflight } from "../components/ReviewPreflight";

/**
 * The chat atoms an assistant message carries, drawn.
 *
 * warhol's `render_component` puts validated atoms on the message and christo
 * mirrors them onto `AskPunttMessage.components`; this is the dispatch from an
 * id to the Carrie component that draws it. Payloads are data only, so every
 * callback an atom wants is decided here, and there is exactly one thing a
 * callback can do: send the person's next message, in words, through
 * `onSubmitAnswers`. Nothing structured rides back (warhol spec
 * kevin.johnson/0001), and no atom carries a status -- a form is answerable
 * on the last assistant turn and read-only history everywhere else.
 */
export type ChatAtomsProps = {
  messageId: string;
  atoms: AskPunttChatAtom[];
  /** Live review id from the assistant message — hydrates the review-list-card. */
  reviewId?: string | null;
  /** True on the last assistant turn: forms take an answer. */
  interactive?: boolean;
  /** Disable form controls while a send is in flight. */
  disabled?: boolean;
  /** The person's next message, in words -- what a submitted form becomes. */
  onSubmitAnswers?: (
    formattedAnswers: string,
    attachments?: QuestionAttachment[],
  ) => void;
};

const NESTED_ONLY = new Set<string>(ASK_PUNTT_NESTED_ONLY_CHAT_ATOM_IDS);

/** True when Carrie can draw this atom on its own, without a parent slot. */
export function isStandaloneChatAtom(atom: AskPunttChatAtom): boolean {
  return !NESTED_ONLY.has(atom.componentId);
}

export {
  chatAtomFromRenderComponentArgs,
  chatAtomsFromAgentSteps,
  collectDrawableChatAtoms,
  mergeChatAtoms,
} from "./renderComponentAtoms";

export function ChatAtoms({
  messageId,
  atoms,
  reviewId,
  interactive = false,
  disabled = false,
  onSubmitAnswers,
}: ChatAtomsProps) {
  const drawable = atoms.filter(isStandaloneChatAtom);
  if (drawable.length === 0) return null;
  return (
    <div
      className="mt-3 flex flex-col gap-3"
      data-testid={`chat-message-atoms-${messageId}`}
    >
      {drawable.map((atom, index) => (
        <div
          key={`${atom.instanceId}-${index}`}
          data-testid={`chat-message-atom-${messageId}-${index}`}
          data-component-id={atom.componentId}
        >
          <ChatAtom
            atom={atom}
            messageId={messageId}
            reviewId={reviewId}
            testIdPrefix={`chat-atom-${messageId}-${index}`}
            interactive={interactive}
            disabled={disabled}
            onSubmitAnswers={onSubmitAnswers}
          />
        </div>
      ))}
    </div>
  );
}

type ChatAtomProps = Pick<
  ChatAtomsProps,
  "interactive" | "disabled" | "onSubmitAnswers" | "messageId" | "reviewId"
> & {
  atom: AskPunttChatAtom;
  /** Unique per message and position, so two atoms of one kind never collide. */
  testIdPrefix: string;
};

function ChatAtom({
  atom,
  messageId,
  reviewId,
  testIdPrefix,
  interactive,
  disabled,
  onSubmitAnswers,
}: ChatAtomProps) {
  switch (atom.componentId) {
    case "ask-puntt.chat-atom.question-form":
      return (
        <>
          <QuestionForm
            data-testid={`${testIdPrefix}-question-form`}
            questions={atom.payload.questions.map(toUserQuestion)}
            optionLayout={atom.payload.optionLayout}
            onSubmit={onSubmitAnswers ?? noop}
            readOnly={!interactive}
            disabled={disabled}
          />
          <FormWaitsForTurnNotice
            testId={`${testIdPrefix}-question-form-waits`}
            interactive={interactive}
            disabled={disabled}
          />
        </>
      );
    case "ask-puntt.chat-atom.review-preflight":
      return (
        <>
          <ChatAtomReviewPreflight
            atom={atom}
            messageId={messageId}
            testIdPrefix={`${testIdPrefix}-review-preflight`}
            interactive={interactive}
            disabled={disabled}
            onSubmitAnswers={onSubmitAnswers}
          />
          <FormWaitsForTurnNotice
            testId={`${testIdPrefix}-review-preflight-waits`}
            interactive={interactive}
            disabled={disabled}
          />
        </>
      );
    case "ask-puntt.chat-atom.enterprise-impact":
      return (
        <ChatAtomEnterpriseImpact
          atom={atom}
          testIdPrefix={`${testIdPrefix}-enterprise-impact`}
        />
      );
    case "ask-puntt.chat-atom.feed-memory-card":
      return <ChatAtomFeedMemoryCard atom={atom} testIdPrefix={testIdPrefix} />;
    case "ask-puntt.chat-atom.review-list-card":
      return (
        <ChatAtomReviewListCard
          atom={atom}
          reviewId={reviewId}
          // why: "still generating" is a property of the turn, not of the id.
          // `interactive` is the last assistant turn and `disabled` is
          // "a send / agent run is in flight", so the two together are the
          // only in-flight signal this surface already receives.
          isTurnPending={!!interactive && !!disabled}
          testIdPrefix={`${testIdPrefix}-review-card`}
        />
      );
    case "ask-puntt.chat-atom.metric-block":
      return (
        <MetricBlock
          data-testid={`${testIdPrefix}-metric-block`}
          label={atom.payload.label}
          value={atom.payload.value}
          // why-not: no `|| undefined` shortcut on `trend` alone -- "" is warhol's "no trend" and MetricBlock's TrendInput has no empty member.
          trend={atom.payload.trend ? atom.payload.trend : undefined}
          size={atom.payload.size}
        />
      );
    // Nested-only atoms never reach here: `isStandaloneChatAtom` filters them
    // and warhol refuses to draw them alone.
    case "ask-puntt.chat-atom.choice-row":
    case "ask-puntt.chat-atom.question-form-card":
      return null;
    default:
      return assertNever(atom);
  }
}

function toUserQuestion(
  question: AskPunttChatAtomOf<"ask-puntt.chat-atom.question-form">["payload"]["questions"][number],
): AskPunttUserQuestion {
  return {
    question: question.question,
    header: question.header,
    options: question.options.map((option) => ({
      label: option.label,
      description: option.description ?? "",
      ...(option.value ? { value: option.value } : {}),
    })),
    multiSelect: question.multiSelect,
    allowFiles: question.allowFiles,
  };
}

function ChatAtomReviewPreflight({
  atom,
  messageId,
  testIdPrefix,
  interactive,
  disabled,
  onSubmitAnswers,
}: {
  atom: AskPunttChatAtomOf<"ask-puntt.chat-atom.review-preflight">;
  messageId: string;
  testIdPrefix: string;
  interactive?: boolean;
  disabled?: boolean;
  onSubmitAnswers?: ChatAtomsProps["onSubmitAnswers"];
}) {
  const { t } = useTranslation("ui");
  const { payload } = atom;
  const [variant, setVariant] = useState<"confirm" | "scope">(
    payload.variant ?? "confirm",
  );
  const defaultSelectedIds =
    payload.selectedCategoryIds ??
    payload.categories.map((category) => category.id);
  const [selectedIds, setSelectedIds] = useState<string[]>(defaultSelectedIds);
  const [submitted, setSubmitted] = useState(false);
  const canStart = interactive && onSubmitAnswers != null && !submitted;
  const canEditScope = interactive && !submitted;

  useEffect(() => {
    const persisted = readReviewPreflightState(messageId);
    if (!persisted) return;
    setSelectedIds(persisted.selectedIds);
    setSubmitted(persisted.submitted);
  }, [messageId]);

  const persist = useCallback(
    (nextSelectedIds: string[], nextSubmitted: boolean) => {
      writeReviewPreflightState(messageId, {
        selectedIds: nextSelectedIds,
        submitted: nextSubmitted,
      });
    },
    [messageId],
  );
  const [otherChecked, setOtherChecked] = useState(false);
  const [focus, setFocus] = useState("");

  const start = () => {
    if (!canStart) return;
    const labels = payload.categories
      .filter((category) => selectedIds.includes(category.id))
      .map((category) => category.label)
      .join(", ");
    const header = t("reviewPreflight.startReview");
    setSubmitted(true);
    persist(selectedIds, true);
    // The same `**Header:** answer` line QuestionForm submits, so the
    // transcript reads the same whichever atom asked. The Other focus rides
    // as a second such line; warhol's review skill reads it off the plan.
    const plan = labels ? `**${header}:** ${labels}` : `**${header}**`;
    const trimmedFocus = otherChecked ? focus.trim() : "";
    onSubmitAnswers(
      trimmedFocus
        ? `${plan}\n**${t("reviewPreflight.focusLabel")}:** ${trimmedFocus}`
        : plan,
    );
  };

  return (
    <ReviewPreflight
      testIdPrefix={testIdPrefix}
      variant={variant}
      categories={payload.categories}
      selectedCategoryIds={selectedIds}
      summary={payload.title || undefined}
      description={payload.description || undefined}
      otherChecked={otherChecked}
      onToggleOther={() => {
        if (canEditScope) setOtherChecked((current) => !current);
      }}
      focusValue={focus}
      onFocusChange={(value) => {
        if (canEditScope) setFocus(value);
      }}
      interactive={interactive}
      disabled={disabled}
      submitted={submitted}
      onStart={start}
      onChangeScope={() => {
        if (canEditScope) setVariant("scope");
      }}
      onCancel={() => setVariant("confirm")}
      onToggleCategory={(id) => {
        if (!canEditScope) return;
        setSelectedIds((current) => {
          const next = current.includes(id)
            ? current.filter((selected) => selected !== id)
            : [...current, id];
          persist(next, submitted);
          return next;
        });
      }}
    />
  );
}

function ChatAtomReviewListCard({
  atom,
  reviewId,
  isTurnPending = false,
  testIdPrefix,
}: {
  atom: AskPunttChatAtomOf<"ask-puntt.chat-atom.review-list-card">;
  reviewId?: string | null;
  /** True while this message's turn is still running — gates the spinner. */
  isTurnPending?: boolean;
  testIdPrefix: string;
}) {
  const ctx = useAskPunttArtifactContext();
  const { payload } = atom;
  // why: warhol invents payload.id (e.g. "current-review") before christo mints
  // the AskPunttReview — never open or fetch on that invented slug
  const resolvedReviewId = reviewId?.trim() ? reviewId.trim() : null;
  const { data } = useAskPunttReview(resolvedReviewId, {
    enabled: !!resolvedReviewId,
  });
  const live = data?.review;
  const thumbnailUrl = resolveReviewListCardThumbnail({
    review: live,
    payloadThumbnailUrl: payload.thumbnailUrl,
    isDemoEnterprise: isDemoEnterprise(),
  });

  const handleOpen = () => {
    if (!resolvedReviewId || !ctx?.openArtifact) return;
    ctx.openArtifact(
      { kind: "askPunttReview", reviewId: resolvedReviewId },
      "message_chip",
    );
  };

  const cardId = resolvedReviewId ?? payload.id;
  // why: no persisted review id means there is nothing to open, so the card
  // never clicks through. It only *generates* while the turn is still
  // running -- christo lands the answer without a review on
  // WarholReviewBuildError, and useAskPunttReview is not even mounted
  // (`enabled: !!resolvedReviewId`), so a settled card with no id would
  // otherwise spin and announce "generating" forever.
  const isMissingReviewId = !resolvedReviewId;
  const isWaitingForReviewId = isMissingReviewId && isTurnPending;

  return (
    <ReviewListCard
      testIdPrefix={testIdPrefix}
      data-testid={`${testIdPrefix}-${cardId}`}
      onClick={resolvedReviewId ? handleOpen : undefined}
      item={{
        id: cardId,
        name: live?.title?.trim() || payload.name,
        versionNumber: payload.versionNumber ?? undefined,
        thumbnailUrl,
        status: payload.status || undefined,
        aiCommentCount: live?.commentsSummary?.ai ?? payload.aiCommentCount,
        humanCommentCount:
          live?.commentsSummary?.human ?? payload.humanCommentCount,
        disabled: isMissingReviewId,
        isGenerating: isWaitingForReviewId,
      }}
    />
  );
}

function ChatAtomEnterpriseImpact({
  atom,
  testIdPrefix,
}: {
  atom: AskPunttChatAtomOf<"ask-puntt.chat-atom.enterprise-impact">;
  testIdPrefix: string;
}) {
  // why: EnterpriseImpact treats a present `open` as controlled, so the payload's initial state has to live in state here or the accordion freezes.
  const [open, setOpen] = useState(atom.payload.open ?? true);
  return (
    <EnterpriseImpact
      testIdPrefix={testIdPrefix}
      metrics={atom.payload.metrics}
      open={open}
      onOpenChange={setOpen}
    />
  );
}

function ChatAtomFeedMemoryCard({
  atom,
  testIdPrefix,
}: {
  atom: AskPunttChatAtomOf<"ask-puntt.chat-atom.feed-memory-card">;
  testIdPrefix: string;
}) {
  const { payload } = atom;
  // why-not: no `edit` / `changeScope` here -- those variants draw a Save that needs a persist callback this surface does not have; the person edits by saying so in the next message.
  const variant = payload.variant === "editedOpen" ? "editedOpen" : "open";
  return (
    <FeedMemoryCard
      variant={variant}
      event={{
        _id: testIdPrefix,
        projectId: "",
        type: "memory_saved",
        actor: { kind: "puntt" },
        visibility: "all",
        createdAt: "",
        payload: { instruction: payload.instruction },
      }}
    />
  );
}

/**
 * why: a form drawn on the turn that is STILL RUNNING is live and last, so it
 * renders enabled-looking — but the turn that drew it has not settled, and
 * `send()` refuses a send made in flight. Rendered with no line, that is a
 * question the person answers into a void: the click is dropped and nothing
 * comes back. This is the visible reason.
 *
 * `interactive && disabled` is exactly that state, and it is why the two must be
 * read together: `disabled` alone also covers forms on turns that have already
 * ENDED (the person replied, so the form is history), and those are read-only,
 * not waiting — telling someone "Puntt will ask when it's done" on a locked
 * historical form would be a lie.
 */
function FormWaitsForTurnNotice({
  testId,
  interactive,
  disabled,
}: {
  testId: string;
  interactive?: boolean;
  disabled?: boolean;
}) {
  const { t } = useTranslation("ui");
  if (!interactive || !disabled) return null;
  return (
    <p className="mt-2 text-xs text-puntt-neutral-gray-11" data-testid={testId}>
      {t("askV2.transport.formWaitsForTurn")}
    </p>
  );
}

function noop(): void {}

function assertNever(value: never): never {
  throw new Error(`Unknown chat atom: ${JSON.stringify(value)}`);
}
